<template>
  <div>
    <transition name="fade">
      <v-overlay :z-index="201" v-if="showEditSelection"></v-overlay>
    </transition>
    <v-dialog persistent autofocus :value="showEditSelection" hide-overlay>
      <v-card class="unmarkable">
        <v-card-title>
          <v-icon>
            border_color
          </v-icon>
          &nbsp; Edit selection
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-tabs centered grow v-model="currentTab">
            <v-tab key="bulkEdit" @click="focus = 'bulkEditItem'">{{
              editValueTabTitle
            }}</v-tab>
            <v-tab
              key="createRamp"
              :disabled="!isSelectionContinuous"
              @click="focus = 'rampStartItem'"
              >Create ramp</v-tab
            >
            <v-tabs-items v-model="currentTab">
              <v-tab-item key="bulkEditItem">
                <v-card flat>
                  <v-card-text>
                    <NumberInput
                      :value="bulkEditItem.value"
                      @valueChange="newValue => (bulkEditItem.value = newValue)"
                      @valueInput="
                        newValue => (bulkEditItem.current = newValue)
                      "
                      @valid="isValid => (bulkEditItem.valid = isValid)"
                      label="Value"
                      :min="minValue"
                      :max="maxValue"
                      :enforceMinMax="valueHasMinMax"
                      :showUnit="showUnit"
                      :digitsAfterComma="digitsAfterComma"
                      ref="bulkEditItem"
                    ></NumberInput>
                  </v-card-text>
                </v-card>
              </v-tab-item>
              <v-tab-item key="createRampItem">
                <v-card flat>
                  <v-card-text>
                    <NumberInput
                      :value="rampStartItem.value"
                      @valueChange="
                        newValue => (rampStartItem.value = newValue)
                      "
                      @valueInput="
                        newValue => (rampStartItem.current = newValue)
                      "
                      @valid="isValid => (rampStartItem.valid = isValid)"
                      label="Ramp start value"
                      :min="minValue"
                      :max="maxValue"
                      :enforceMinMax="valueHasMinMax"
                      :showUnit="showUnit"
                      :digitsAfterComma="digitsAfterComma"
                      ref="rampStartItem"
                    ></NumberInput>
                    <NumberInput
                      :value="rampEndItem.value"
                      @valueChange="newValue => (rampEndItem.value = newValue)"
                      @valueInput="newValue => (rampEndItem.current = newValue)"
                      @valid="isValid => (rampEndItem.valid = isValid)"
                      label="Ramp end value"
                      :min="minValue"
                      :max="maxValue"
                      :enforceMinMax="valueHasMinMax"
                      :showUnit="showUnit"
                      :digitsAfterComma="digitsAfterComma"
                      ref="rampEndItem"
                    ></NumberInput>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-tabs>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" text @click="toggleShowEditSelection"
            >Cancel</v-btn
          >
          <v-btn
            color="secondary darken-1"
            text
            @click="saveSelection"
            :disabled="!canSave"
            >Apply changes
            <v-progress-circular
              v-if="loading"
              indeterminate
            ></v-progress-circular
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const _ = require("lodash");
import { EDIT_VALUES } from "../../store/modules/uptData/constants";
import { TOGGLE_SHOW_EDIT_SELECTION } from "../../store/modules/showComponents/constants";
import { pickTypesSettings } from "../utils/tableEditorHeaderHelper.js";
import NumberInput from "./helpers/NumberInput";
import { notificationMixin } from "../mixins/notificationMixin.js";
import selection from "../utils/nonReactiveState/selection";
import uptFile from "../utils/nonReactiveState/uptFile";

const editTypes = ["bulkEdit", "createRamp"];

const getInitialData = () => {
  return {
    initialValues: [],
    currentValues: [],
    currentSelections: [],
    valueHasMinMax: false,
    minValue: 0,
    maxValue: 0,
    currentTab: null,
    changed: false,
    bulkEditItem: { value: 0, current: 0, valid: true },
    rampStartItem: { value: 0, valid: true },
    rampEndItem: { value: 0, valid: true },
    showUnit: null,
    digitsAfterComma: 0,
    focus: false
  };
};

export default {
  name: "EditSelection",

  mixins: [notificationMixin],

  components: { NumberInput },

  data: getInitialData,

  updated: function() {
    if (this.focus) {
      this.focusInput(this.focus);
    }
  },

  computed: {
    hasChanges() {
      let index;
      switch (editTypes[this.currentTab]) {
        case "bulkEdit":
          index = _.findIndex(
            this.currentValues,
            v => v !== this.bulkEditItem.current
          );
          return index !== -1;
        case "createRamp":
          return true;
        default:
          return false;
      }
    },
    valid() {
      switch (editTypes[this.currentTab]) {
        case "bulkEdit":
          return this.bulkEditItem.valid;
        case "createRamp":
          return this.rampStartItem.valid && this.rampEndItem.valid;
        default:
          return false;
      }
    },
    canSave() {
      return this.hasChanges && this.valid;
    },
    showEditSelection() {
      return this.$store.state.showComponents.showEditSelection;
    },
    currentEditType() {
      return editTypes[this.currentTab];
    },
    isSelectionContinuous() {
      return this.$store.state.selection.isSelectionContinuous;
    },
    editValueTabTitle() {
      if (this.$store.state.selection.selectionLength <= 1) {
        return "Edit single value";
      }
      return "Edit all values";
    },
    loading() {
      return this.$store.state.uptData.isApplyingChanges;
    }
  },

  watch: {
    showEditSelection(newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.currentTab = null;
        this.currentSelections = selection.selectionList;

        const dataTypeIndex =
          uptFile.pickData.driveData.tagNames[
            _.head(this.currentSelections)[1]
          ];
        const dataType = pickTypesSettings[dataTypeIndex];

        const valuesToEdit = [];
        this.currentSelections.map(s => {
          valuesToEdit.push(uptFile.pickData.driveData.data[s[0]][s[1]]);
        });

        this.bulkEditItem.value = this.bulkEditItem.current = _.head(
          valuesToEdit
        );
        this.rampStartItem.value = this.rampStartItem.current = _.min(
          valuesToEdit
        );
        this.rampEndItem.value = this.rampEndItem.current = _.max(valuesToEdit);
        this.initialValues = valuesToEdit;
        this.currentValues = valuesToEdit;
        this.valueHasMinMax = dataType.typeSetting.hasMinMax;
        this.minValue = dataType.typeSetting.minValue;
        this.maxValue = dataType.typeSetting.maxValue;
        this.showUnit = dataType.showUnit;
        this.digitsAfterComma = dataType.digitsAfterComma;

        if (this.$refs.bulkEditItem) {
          this.$refs.bulkEditItem.reset();
        }
        if (this.$refs.rampStartItem) {
          this.$refs.rampStartItem.reset();
        }
        if (this.$refs.rampEndItem) {
          this.$refs.rampEndItem.reset();
        }
        this.focus = "bulkEditItem";
      }
    }
  },

  methods: {
    toggleShowEditSelection() {
      this.$store.dispatch(TOGGLE_SHOW_EDIT_SELECTION);
      Object.assign(this, getInitialData());
    },
    saveSelection() {
      let sortedSelection;
      const values = [];
      switch (editTypes[this.currentTab]) {
        case "bulkEdit":
          this.currentSelections.map(() => {
            values.push(this.bulkEditItem.value);
          });
          this.$store.dispatch(EDIT_VALUES, {
            values,
            selections: this.currentSelections
          });
          break;
        case "createRamp":
          sortedSelection = _.sortBy(this.currentSelections, s => s[0]);
          sortedSelection.map((selectedItem, index) => {
            const value =
              this.rampStartItem.value +
              ((this.rampEndItem.value - this.rampStartItem.value) /
                (sortedSelection.length - 1)) *
                index;
            values.push(value);
          });
          this.$store.dispatch(EDIT_VALUES, {
            values,
            selections: sortedSelection
          });
          break;
        default:
          this.errorNotification("Data could not be saved.");
      }
      this.$store.dispatch(TOGGLE_SHOW_EDIT_SELECTION);
    },
    async focusInput(input) {
      let numberOfTries = 0;
      while (numberOfTries < 5) {
        numberOfTries++;
        if (this.focus && this.$refs[input]) {
          this.focus = false;
          this.$nextTick(() => this.$refs[input].focus());
          break;
        } else {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style></style>
